import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { Trash2, AlertCircle, Ban, Plus, Pencil, Send } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import {
  createNegativeKeywordList,
  renameNegativeKeywordList,
  deleteNegativeKeywordList,
  addNegativeKeywordListKeywords,
  removeNegativeKeywordListKeyword,
} from '@/lib/apis';
import type { NegativeKeywordList, SharedNegativeKeyword } from '@/lib/types';
import { NoGoogleAdsAccountSelected } from '@/components/glitch-ui/no-google-ads-account';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { ApplyListToCampaignsModal } from '@/components/settings/account/apply-list-to-campaigns-modal';
import { googleAdsAccountAtom } from '@/store';
import {
  negativeKeywordListsQueryOptions,
  negativeKeywordListKeywordsQueryOptions,
} from '@/lib/query-options';

const MATCH_TYPES = ['EXACT', 'PHRASE', 'BROAD'] as const;
type MatchType = (typeof MATCH_TYPES)[number];
const MAX_LISTS = 20;
const KEYWORD_CAP = 5000;

const formatMatchType = (m: string) => m.charAt(0) + m.slice(1).toLowerCase();

const getMatchTypePillStyle = (matchType: string) => {
  switch (matchType?.toUpperCase()) {
    case 'EXACT': return 'bg-purple-50 text-purple-600';
    case 'PHRASE': return 'bg-blue-50 text-blue-600';
    case 'BROAD': return 'bg-gray-100 text-gray-600';
    default: return 'bg-gray-100 text-gray-600';
  }
};

function parseKeywords(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((k) => k.trim())
    .filter((k) => k.length > 0);
}

export interface NegativeKeywordListsRef {
  openAdd: () => void;
}

interface NegativeKeywordListsProps {
  selectedListId: string | null;
  onSelectList: (id: string | null) => void;
}

/**
 * Master panel: the list of negative keyword lists. Selection is controlled by the parent so the
 * detail panel ({@link NegativeKeywordDetail}) can render as a sibling column outside this card,
 * matching the master-detail layout used on landing pages, competitors, and optimizations.
 */
export const NegativeKeywordLists = forwardRef<NegativeKeywordListsRef, NegativeKeywordListsProps>(
  ({ selectedListId, onSelectList }, ref) => {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
    const accountId = googleAdsAccount?.googleads_account_id;

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [createError, setCreateError] = useState<string | undefined>();
    const [listToDelete, setListToDelete] = useState<NegativeKeywordList | null>(null);
    const [applyTarget, setApplyTarget] = useState<NegativeKeywordList | null>(null);

    const { data: listsData, isLoading, isError } = useQuery(negativeKeywordListsQueryOptions(accountId));

    const lists = useMemo(() => listsData?.lists ?? [], [listsData]);

    const invalidateLists = () =>
      queryClient.invalidateQueries({ queryKey: negativeKeywordListsQueryOptions(accountId).queryKey });

    const createMutation = useMutation({
      mutationFn: (name: string) => createNegativeKeywordList(name),
      onSuccess: (created) => {
        invalidateLists();
        setIsCreateOpen(false);
        setNewListName('');
        onSelectList(created.id);
        toast({ title: 'List created', description: `"${created.name}" is ready for keywords.` });
      },
      onError: (error: Error) => {
        toast({ title: 'Failed to create list', description: error.message, variant: 'destructive' });
      },
    });

    const deleteListMutation = useMutation({
      mutationFn: (id: string) => deleteNegativeKeywordList(id),
      onSuccess: (_data, id) => {
        invalidateLists();
        queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        if (selectedListId === id) onSelectList(null);
        setListToDelete(null);
      },
      onError: (error: Error) => {
        toast({ title: 'Failed to delete list', description: error.message, variant: 'destructive' });
      },
    });

    // Tell-on-click validation: Save stays enabled (except the MAX_LISTS hard cap);
    // clicking it marks the missing field instead of sitting silently disabled.
    const handleCreate = () => {
      if (!newListName.trim()) {
        setCreateError('Required');
        return;
      }
      createMutation.mutate(newListName.trim());
    };

    useImperativeHandle(ref, () => ({ openAdd: () => { setCreateError(undefined); setIsCreateOpen(true); } }), []);

    const listColumns = useMemo((): ColumnDef<NegativeKeywordList>[] => [
      {
        accessorKey: 'name',
        header: `Negatives (${lists.length}/20)`,
        meta: { className: 'px-6 w-[45%] whitespace-normal' },
        cell: ({ row }) => <span className="text-sm font-medium text-gray-900">{row.original.name}</span>,
      },
      {
        accessorKey: 'member_count',
        header: 'Keywords',
        meta: { className: 'px-6 w-[15%]' },
        cell: ({ row }) => <span className="text-sm text-gray-700">{row.original.member_count}</span>,
      },
      {
        accessorKey: 'applied_campaign_count',
        header: 'Campaigns',
        meta: { className: 'px-6 w-[15%]' },
        cell: ({ row }) => <span className="text-sm text-gray-700">{row.original.applied_campaign_count}</span>,
      },
      {
        id: 'actions',
        enableSorting: false,
        meta: { className: 'px-6 w-[25%] text-right' },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-gray-900"
              onClick={(e) => { e.stopPropagation(); setApplyTarget(row.original); }}
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Apply
            </Button>
            <IconButton
              variant="destructive"
              aria-label="Delete negative keyword list"
              onClick={(e) => { e.stopPropagation(); setListToDelete(row.original); }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>
        ),
      },
    ], [lists.length]);

    if (!googleAdsAccount?.googleads_account_id) {
      return (
        <NoGoogleAdsAccountSelected description="A Google Ads account needs to be selected for this organization before negative keyword lists can be managed." />
      );
    }

    if (isError) {
      return (
        <div className="bg-white shadow-sm border border-red-200 rounded-lg overflow-hidden">
          <div className="p-6 flex items-center gap-3 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">Failed to load negative keyword lists</span>
          </div>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="border rounded-lg bg-white overflow-hidden">
          <div className="flex items-center justify-center gap-3 py-16">
            <Spinner size="md" className="text-gray-500" />
            <span className="text-gray-500">Loading negative keyword lists...</span>
          </div>
        </div>
      );
    }

    return (
      <>
        <DataTable
          data={lists}
          columns={listColumns}
          variant="flush"
          pageSize={10}
          onRowClick={(row) => onSelectList(row.id)}
          getRowClassName={(row) => (row.id === selectedListId ? 'bg-[#290BD9]/10' : '')}
          emptyIcon={Ban}
          emptyTitle="No Negative Keyword Lists"
          emptyDescription="Create a named list of negative keywords, then apply it to the campaigns where those terms should be excluded."
        />

        {/* Create List */}
        <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); setCreateError(undefined); }}>
          <DialogContent className="sm:max-w-[440px]" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>New Negative Keyword List</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-4">
              <div className="flex items-center justify-between">
                <Label>List Name</Label>
                <FieldError message={createError} />
              </div>
              <div className="relative">
                <Input
                  value={newListName}
                  onChange={(e) => { setNewListName(e.target.value); setCreateError(undefined); }}
                  placeholder="e.g. Competitors"
                  autoFocus
                  error={!!createError}
                  className={createError ? 'pr-8' : ''}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreate();
                  }}
                />
                <InputAdornment error={!!createError} />
              </div>
              {lists.length >= MAX_LISTS && (
                <p className="text-xs text-red-600">Google Ads allows at most {MAX_LISTS} lists per account.</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)} disabled={createMutation.isPending}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={createMutation.isPending || lists.length >= MAX_LISTS}
                className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
              >
                {createMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConformationModal
          isOpen={!!listToDelete}
          onClose={() => setListToDelete(null)}
          onConfirm={() => listToDelete && deleteListMutation.mutate(listToDelete.id)}
          title="Delete Negative Keyword List"
          description={
            listToDelete
              ? `Delete "${listToDelete.name}"? This removes it from ${listToDelete.applied_campaign_count} campaign${listToDelete.applied_campaign_count !== 1 ? 's' : ''}, and those campaigns will no longer exclude these keywords.`
              : ''
          }
          confirmText="Delete"
          cancelText="Cancel"
          isLoading={deleteListMutation.isPending}
        />

        {applyTarget && (
          <ApplyListToCampaignsModal
            isOpen={!!applyTarget}
            onClose={() => setApplyTarget(null)}
            list={applyTarget}
          />
        )}
      </>
    );
  },
);
NegativeKeywordLists.displayName = 'NegativeKeywordLists';

interface NegativeKeywordDetailProps {
  listId: string | null;
}

/**
 * Detail panel: the keywords in the selected list. Renders as the right-hand column beside the
 * {@link NegativeKeywordLists} master, outside its card. Shows a "Select a List" prompt until a
 * list is chosen.
 */
export function NegativeKeywordDetail({ listId }: NegativeKeywordDetailProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const accountId = googleAdsAccount?.googleads_account_id;

  const [renameTarget, setRenameTarget] = useState<NegativeKeywordList | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | undefined>();
  const [isAddKeywordsOpen, setIsAddKeywordsOpen] = useState(false);
  const [bulkKeywords, setBulkKeywords] = useState('');
  const [keywordsError, setKeywordsError] = useState<string | undefined>();
  const [matchType, setMatchType] = useState<MatchType>('PHRASE');
  const [keywordToDelete, setKeywordToDelete] = useState<SharedNegativeKeyword | null>(null);

  const { data: listsData } = useQuery(negativeKeywordListsQueryOptions(accountId));

  const selectedList = useMemo(
    () => listsData?.lists.find((l) => l.id === listId) ?? null,
    [listsData, listId],
  );

  const { data: keywordsData, isLoading: isLoadingKeywords } = useQuery(
    negativeKeywordListKeywordsQueryOptions(accountId, listId),
  );

  const invalidateLists = () =>
    queryClient.invalidateQueries({ queryKey: negativeKeywordListsQueryOptions(accountId).queryKey });
  const invalidateKeywords = (id: string) =>
    queryClient.invalidateQueries({
      queryKey: negativeKeywordListKeywordsQueryOptions(accountId, id).queryKey,
    });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameNegativeKeywordList(id, name),
    onSuccess: () => {
      invalidateLists();
      setRenameTarget(null);
      setRenameValue('');
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to rename list', description: error.message, variant: 'destructive' });
    },
  });

  const addKeywordsMutation = useMutation({
    mutationFn: ({ id, keywords, mt }: { id: string; keywords: string[]; mt: string }) =>
      addNegativeKeywordListKeywords(id, keywords, mt),
    onSuccess: (_data, { id }) => {
      invalidateKeywords(id);
      invalidateLists();
      setIsAddKeywordsOpen(false);
      setBulkKeywords('');
      setMatchType('EXACT');
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to add keywords', description: error.message, variant: 'destructive' });
    },
  });

  const removeKeywordMutation = useMutation({
    mutationFn: ({ id, resourceName }: { id: string; resourceName: string }) =>
      removeNegativeKeywordListKeyword(id, resourceName),
    onSuccess: (_data, { id }) => {
      invalidateKeywords(id);
      invalidateLists();
      setKeywordToDelete(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to remove keyword', description: error.message, variant: 'destructive' });
    },
  });

  // Tell-on-click validation: Save stays enabled while idle; clicking it marks the
  // missing/invalid field instead of sitting silently disabled.
  const handleRename = () => {
    if (!renameTarget) return;
    if (!renameValue.trim()) {
      setRenameError('Required');
      return;
    }
    renameMutation.mutate({ id: renameTarget.id, name: renameValue.trim() });
  };

  const handleAddKeywords = () => {
    if (!listId) return;
    const keywords = parseKeywords(bulkKeywords);
    if (keywords.length === 0) {
      setKeywordsError(bulkKeywords.trim() ? 'No valid keywords found' : 'Required');
      return;
    }
    addKeywordsMutation.mutate({ id: listId, keywords, mt: matchType });
  };

  const keywordColumns = useMemo((): ColumnDef<SharedNegativeKeyword>[] => [
    {
      accessorKey: 'keyword',
      header: 'Keyword',
      meta: { className: 'px-6 w-[55%] whitespace-normal' },
      cell: ({ row }) => <span className="text-sm font-medium text-gray-900">{row.original.keyword}</span>,
    },
    {
      accessorKey: 'match_type',
      header: 'Match Type',
      meta: { className: 'px-6 w-[35%]' },
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMatchTypePillStyle(row.original.match_type)}`}>
          {formatMatchType(row.original.match_type)}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      meta: { className: 'px-6 w-[10%] text-right' },
      cell: ({ row }) => (
        <div className="flex justify-end">
          <IconButton
            variant="destructive"
            aria-label="Delete keyword"
            onClick={() => setKeywordToDelete(row.original)}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </div>
      ),
    },
  ], []);

  if (!selectedList) {
    return (
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
        <Ban className="mx-auto h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
        <h3 className="text-lg font-semibold text-gray-500 mb-2">Select a List</h3>
        <p className="text-gray-500">Click any list to view and manage its negative keywords</p>
      </div>
    );
  }

  const keywords = keywordsData?.keywords ?? [];
  const parsedCount = parseKeywords(bulkKeywords).length;
  const atKeywordCap = selectedList.member_count >= KEYWORD_CAP;

  return (
    <>
      <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate">{selectedList.name}</h3>
            <button
              onClick={() => { setRenameTarget(selectedList); setRenameValue(selectedList.name); setRenameError(undefined); }}
              className="p-1 text-gray-400 hover:text-gray-700 rounded transition-colors cursor-pointer shrink-0"
              title="Rename list"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
          <Button
            variant="outline"
            className="h-9 text-gray-900 shrink-0"
            onClick={() => { setKeywordsError(undefined); setIsAddKeywordsOpen(true); }}
            disabled={atKeywordCap}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Negatives
          </Button>
        </div>
        <div className="px-6 py-2 border-b border-gray-200 text-sm text-gray-500">
          {selectedList.member_count} keyword{selectedList.member_count !== 1 ? 's' : ''} · applied to{' '}
          {selectedList.applied_campaign_count} campaign{selectedList.applied_campaign_count !== 1 ? 's' : ''}
        </div>
        {isLoadingKeywords ? (
          <div className="flex items-center justify-center gap-3 py-12">
            <Spinner size="md" className="text-gray-500" />
            <span className="text-gray-500">Loading keywords...</span>
          </div>
        ) : (
          // px-6 pb-6 is the padding flush's -mx-6/-mb-6 cancels: table chrome
          // bleeds to the card edges while cell content (px-6) aligns with the header.
          <div className="px-6 pb-6">
            <DataTable
              data={keywords}
              columns={keywordColumns}
              variant="flush"
              pageSize={999}
              emptyIcon={Ban}
              emptyTitle="No Keywords Yet"
              emptyDescription="Add negative keywords to this list, then apply the list to campaigns."
            />
          </div>
        )}
      </div>

      {/* Rename List */}
      <Dialog open={!!renameTarget} onOpenChange={(open) => { if (!open) { setRenameTarget(null); setRenameError(undefined); } }}>
        <DialogContent className="sm:max-w-[440px]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Rename List</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <div className="flex items-center justify-between">
              <Label>List Name</Label>
              <FieldError message={renameError} />
            </div>
            <div className="relative">
              <Input
                value={renameValue}
                onChange={(e) => { setRenameValue(e.target.value); setRenameError(undefined); }}
                autoFocus
                error={!!renameError}
                className={renameError ? 'pr-8' : ''}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRename();
                }}
              />
              <InputAdornment error={!!renameError} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)} disabled={renameMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={handleRename}
              disabled={renameMutation.isPending}
              className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
            >
              {renameMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Keywords */}
      <Dialog open={isAddKeywordsOpen} onOpenChange={(open) => { setIsAddKeywordsOpen(open); setKeywordsError(undefined); }}>
        <DialogContent className="sm:max-w-[500px]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Negative Keywords</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Keywords</Label>
                <FieldError message={keywordsError} />
              </div>
              <Textarea
                value={bulkKeywords}
                onChange={(e) => { setBulkKeywords(e.target.value); setKeywordsError(undefined); }}
                placeholder={'Enter keywords, one per line or comma-separated\nfree trial\ncheap, discount\ndownload'}
                className="min-h-[150px] text-gray-900"
                autoFocus
                error={!!keywordsError}
              />
              {parsedCount > 0 && (
                <p className="text-xs text-gray-500 text-right">{parsedCount} keyword{parsedCount !== 1 ? 's' : ''}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Match Type</Label>
              <Select value={matchType} onValueChange={(v) => setMatchType(v as MatchType)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MATCH_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{formatMatchType(type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Negative keywords are not expanded to synonyms, close variants, or plurals: add each form explicitly.
                Misspellings are covered automatically.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddKeywordsOpen(false)} disabled={addKeywordsMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={handleAddKeywords}
              disabled={addKeywordsMutation.isPending}
              className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
            >
              {addKeywordsMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={!!keywordToDelete}
        onClose={() => setKeywordToDelete(null)}
        onConfirm={() => keywordToDelete && listId && removeKeywordMutation.mutate({ id: listId, resourceName: keywordToDelete.resource_name })}
        title="Remove Negative Keyword"
        description={`Are you sure you want to remove "${keywordToDelete?.keyword}"?`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={removeKeywordMutation.isPending}
      />
    </>
  );
}
